"use client";
import React, { useEffect, useMemo, useState } from "react";
import styles from "./SitemapModule.module.scss";
import formStyles from "../Form/Form.module.scss"; // Styles des Form-Builders
import { Section } from "@/components/Section/Section";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { SitemapGroup } from "@/getData/getSitemapData.ts";
import { SitemapSection } from "@/modules/SitemapModule/SitemapSection/SitemapSection.tsx";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG.client.tsx";
import { OptionsSitemapType } from "@/types/OptionType.ts";
import { InputV2 } from "@/modules/Form/Inputs/Inputs.tsx";

interface SidemapModuleProps {
  sitemapOptions: OptionsSitemapType;
  sitemapData: SitemapGroup[];
}

const SitemapModuleClient = ({
  sitemapOptions,
  sitemapData,
}: SidemapModuleProps) => {
  const { show_search, sitemap_labels: labels } = sitemapOptions;

  const { getIcon } = useGlobalContext();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const filteredSitemap = useMemo(() => {
    const search = debouncedSearchTerm.trim().toLowerCase();
    if (!search) return sitemapData;

    return sitemapData
      .map((group) => {
        const filteredItems = group.items?.filter((item) =>
          item.title.toLowerCase().includes(search),
        );
        const groupMatches = group.label.toLowerCase().includes(search);

        if (groupMatches || (filteredItems && filteredItems.length > 0)) {
          return {
            ...group,
            items: groupMatches ? group.items : filteredItems,
          };
        }
        return null;
      })
      .filter(Boolean) as SitemapGroup[];
  }, [debouncedSearchTerm, sitemapData]);

  const searchColumns =
    "is-12-tablet is-10-desktop is-12-widescreen is-8-fullhd";

  const SearchJsx = show_search && (
    <div className={`columns is-centered ${formStyles["form-builder"]}`}>
      <div
        className={`column ${searchColumns} ${styles["search-wrapper"]} ${formStyles["input"]}`}
      >
        <InputV2
          inputType={"text"}
          name="sitemap-search"
          label={labels.search_label}
          placeholder={labels.search_placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label={labels.search_aria_label}
        />
        <CustomSVG
          src={getIcon("search")}
          className="is-flex"
          wrapper="span"
          width={30}
          height={30}
          aria-hidden="true"
        />
      </div>
    </div>
  );

  return (
    <Section
      id={"sitemap"}
      disableColumns
      containerClassName={`sitemap ${styles["sitemap"]}`}
    >
      {SearchJsx}
      {filteredSitemap.map((group) => (
        <SitemapSection
          key={`${group.postType}-${group.label}`}
          group={group}
        />
      ))}

      {filteredSitemap.length === 0 && (
        <p className={"has-text-centered"}>
          {debouncedSearchTerm
            ? labels.no_search_results
            : labels.no_data_available}
        </p>
      )}
    </Section>
  );
};

export default SitemapModuleClient;
